import {nativeAccountReturnPath} from './native-auth-return.mjs';

const actions = new Set(['login', 'register', 'recover', 'password', 'logout']);
const publicUser = user => user && typeof user.id === 'string' && user.id ? {id:user.id,name:String(user.name || 'Player'),nickname:String(user.nickname || ''),email:String(user.email || '')} : null;
const statusMessages = {
  success:'Checking your sign-in…',
  recovery:'Checking your recovery link…',
  cancelled:'Sign-in was cancelled. You can try again or keep playing.',
  expired:'That sign-in link expired. Start again below.',
  error:'Sign-in could not be completed. Please try again.'
};
/** No provider token, role, or form password becomes native world authority. */
export function createAccountAccess({fetcher=globalThis.fetch,onState=()=>{},onSessionChange=()=>{},onUncertain=()=>{},returnPath='/',returnStatus=null,timeoutMs=20000}={}) {
  let disposed=false,readVersion=0,reading=null,mutating=false;
  let state={kind:'loading',mode:'login',busy:false,message:statusMessages[returnStatus] || '',error:false,user:null,enabled:false,google:false,github:false,emailSignup:false,recovery:false};
  const emit=patch=>{state={...state,...patch};if(!disposed)onState({...state});};
  async function request(action,body,signal) {
    const response=await fetcher('/api/auth/'+action,{method:body?'POST':'GET',credentials:'same-origin',cache:'no-store',redirect:'error',referrerPolicy:'same-origin',headers:body?{'Content-Type':'application/json'}:{},...(body?{body:JSON.stringify(body)}:{}),signal});
    let data;
    try { data=await response.json(); } catch { throw Object.assign(Error('The account service returned an unreadable response. Reload to check your sign-in.'),{uncertain:!!body}); }
    if(!response.ok)throw Object.assign(Error(typeof data?.error==='string'?data.error.slice(0,500):'Sign-in could not be completed. Please try again.'),{definite:true});
    return data;
  }
  async function refresh() {
    if(disposed||mutating)return;
    const version=++readVersion;reading?.abort();const controller=new AbortController();reading=controller;
    const timeout=setTimeout(()=>controller.abort(),timeoutMs);
    emit({kind:'loading'});
    try {
      const auth=await request('me',null,controller.signal);
      if(disposed||version!==readVersion)return;
      if(typeof auth?.enabled!=='boolean'||(auth.user&&!publicUser(auth.user)))throw Error('The account service is temporarily unavailable.');
      const user=publicUser(auth.user);
      const verifiedMessage=returnStatus==='success'?(user?'You’re signed in. Your account is ready.':'Sign-in was not completed. Please try again.'):returnStatus==='recovery'?(user&&auth.recovery===true?'Choose a new password to finish account recovery.':'That recovery link is no longer active. Request a new one below.'):state.message;
      emit({message:verifiedMessage,kind:auth.enabled?user?'authenticated':'guest':'disabled',user,enabled:auth.enabled,google:auth.enabled&&auth.google===true,github:auth.enabled&&auth.github===true,emailSignup:auth.enabled&&auth.emailSignup===true,recovery:!!user&&auth.recovery===true,mode:auth.recovery&&user?'password':'login'});
    } catch(error) {if(!disposed&&version===readVersion)emit({kind:'unavailable',error:true,message:error.name==='AbortError'?'The account service took too long. Try again.':error.message});}
    finally {clearTimeout(timeout);}
  }
  function setMode(mode) {
    if(disposed||mutating||!['login','register','recover'].includes(mode))return;
    if(mode!=='login'&&!state.emailSignup)return;
    emit({mode,error:false,message:''});
  }
  async function submit(action,fields={}) {
    if(disposed||mutating||state.busy||!actions.has(action)||!state.enabled||state.uncertain)return false;
    if(['register','recover'].includes(action)&&!state.emailSignup)return false;
    if(action==='password'&&!state.recovery)return false;
    mutating=true;readVersion++;reading?.abort();
    emit({busy:true,error:false,message:action==='logout'?'Signing out…':action==='recover'?'Sending your recovery link…':'Please wait…'});
    let body=action==='logout'?{}:action==='password'?{password:fields.password}:{email:fields.email,...(action!=='recover'?{password:fields.password}:{}),...(action==='register'?{name:fields.name}:{}),...(['register','recover'].includes(action)?{return_to:nativeAccountReturnPath(returnPath)}:{})};
    const controller=new AbortController(),timeout=setTimeout(()=>controller.abort(),timeoutMs);
    try {
      const result=await request(action,body,controller.signal);body=null;
      const user=publicUser(result?.user);
      if(action==='logout'?result?.user===null:!!user) {
        // A dismissed panel cannot undo a completed cookie mutation. Rebootstrap
        // native identity even if an account-invalidation message closed it.
        onSessionChange({action,user});
        if(!disposed)emit({kind:'refreshing',busy:true,message:action==='logout'?'Signed out. Returning to Valoria…':'Refreshing your Valoria account…'});
        return true;
      }
      if(!['register','recover'].includes(action)||typeof result?.message!=='string')throw Object.assign(Error('The sign-in result could not be confirmed. Reload to check your account.'),{uncertain:true});
      emit({busy:false,error:false,message:typeof result.message==='string'?result.message.slice(0,500):'Check your email to continue.',mode:'login'});
      return true;
    } catch(error) {
      const uncertain=!error.definite;
      const message=uncertain?'The request could not be confirmed. Reload Valoria to check your sign-in before trying again.':error.message;
      emit({busy:false,error:true,uncertain,message});
      if(uncertain)onUncertain({action,message});
      return false;
    } finally {body=null;mutating=false;clearTimeout(timeout);}
  }
  return {refresh,submit,setMode,getState:()=>({...state}),destroy(){disposed=true;readVersion++;reading?.abort();}};
}
